// eslint-disable-next-line max-classes-per-file
import { isValidDate, hoursTo12 } from '../../utils/ids-date-utils/ids-date-utils';
import { stringToBool } from '../../utils/ids-string-utils/ids-string-utils';
import IdsCheckbox from '../ids-checkbox/ids-checkbox';
import IdsDropdown from '../ids-dropdown/ids-dropdown';
import IdsDropdownList from '../ids-dropdown/ids-dropdown-list';
import IdsInput from '../ids-input/ids-input';
import IdsTriggerField from '../ids-trigger-field/ids-trigger-field';
import type IdsButton from '../ids-button/ids-button';
import type IdsDataGridCell from './ids-data-grid-cell';
import type IdsDatePicker from '../ids-date-picker/ids-date-picker';
import type IdsDatePickerPopup from '../ids-date-picker/ids-date-picker-popup';
import type IdsLookup from '../ids-lookup/ids-lookup';
import type IdsModal from '../ids-modal/ids-modal';
import type IdsTimePicker from '../ids-time-picker/ids-time-picker';
import type IdsTimePickerPopup from '../ids-time-picker/ids-time-picker-popup';
import type IdsMultiselect from '../ids-multiselect/ids-multiselect';
import '../ids-time-picker/ids-time-picker';
import '../ids-date-picker/ids-date-picker';
import '../ids-lookup/ids-lookup';
import '../ids-multiselect/ids-multiselect';
import { IdsValidationRule } from '../../mixins/ids-validation-mixin/ids-validation-mixin';
import { attributes, htmlAttributes } from '../../core/ids-attributes';
import IdsIcon from '../ids-icon/ids-icon';
import { getClosest } from '../../utils/ids-dom-utils/ids-dom-utils';
import IdsListBoxOption from '../ids-list-box/ids-list-box-option';
import { IdsDropdownOption, IdsDropdownOptions } from '../ids-dropdown/ids-dropdown-common';
import IdsSplitterPane from '../ids-splitter/ids-splitter-pane';

/** Type for editor input elements across all editor types */
export type IdsDataGridEditors =
  | IdsInput | IdsCheckbox | IdsDropdown | IdsDatePicker | IdsTimePicker
  | IdsLookup | IdsTriggerField;

export interface IdsDataGridEditorOptions {
  /** The type of editor (i.e. text, data, time, dropdown, checkbox, number ect) */
  type: string;
  /** The field in the data set to show */
  field: string;
  /** If true the editor will remain visible */
  inline: boolean;
}

export interface IdsDataGridSaveValue {
  // value to be saved in data set
  value?: string | number | boolean | null | IdsDropdownOptions;
  // (optional) value used for dirty checking
  dirtyCheckValue?: string | number | boolean | null | IdsDropdownOptions;
}

export interface IdsDataGridEditor {
  /** The type of editor (i.e. input, dropdown, checkbox ect) */
  type: string;
  /** The main editor element */
  input?: IdsDataGridEditors;
  /** Optional Popup interface for some cell types */
  popup?: IdsDatePickerPopup | IdsTimePickerPopup | IdsModal;
  /** The function that invokes and sets values on the input */
  init: (cell?: IdsDataGridCell) => void;
  /** Optional callback function that runs after the editor initialization is complete */
  onEndCellEdit?: (cell?: IdsDataGridCell) => void;
  /** The function that transforms and saved the editor */
  save: (cell?: IdsDataGridCell) => IdsDataGridSaveValue | undefined | null;
  /** The function that tears down all aspects of the editor */
  destroy: (cell?: IdsDataGridCell) => void;
  /** MouseEvent if click was used to edit */
  clickEvent?: MouseEvent;
  /** The function that returns the input-element's value */
  value: () => boolean | number | string;
  /** The function that changes the input-element's value */
  change: (newValue: boolean | number | string) => void;
}

const applyEditorSettings = (elem: any, settings?: Record<string, any>) => {
  // eslint-disable-next-line guard-for-in
  for (const setting in settings) {
    elem[setting] = settings[setting];
  }
};

const applyEditorValidation = (elem: any, validation?: Partial<IdsValidationRule>) => {
  if (elem?.addValidationRule && validation) {
    validation.type = 'error';
    elem?.addValidationRule?.(validation);
  }
};

const applyEditorDropdownFocus = async (editor: IdsDataGridEditor) => {
  // For combobox, focus the input field without opening the dropdown
  if (editor.input?.comboBox) {
    const triggerField = editor.input?.container?.querySelector<IdsTriggerField>('ids-trigger-field');
    if (triggerField?.input) {
      triggerField.input.focus();
      return;
    }
  }
  // For regular dropdown or combobox fallback, focus the dropdown component
  editor.input?.focus();
};

const applyEditorPopupFocus = async (editor: IdsDataGridEditor) => {
  const autoOpen = (<HTMLElement>editor.clickEvent?.target)?.classList?.contains('editor-cell-icon');
  if (autoOpen) {
    await editor.popup?.show();
    editor.popup?.focus();
  } else {
    editor.input?.focus();
  }
};

const applyEditorEndCellEdit = (cell: IdsDataGridCell, e: KeyboardEvent) => {
  if (e.code === 'Escape') {
    e.stopImmediatePropagation();
    e.stopPropagation();
    e.preventDefault();
    cell?.cancelCellEdit();
    cell?.focus();
  }
};

/**
 * Whether an element establishes a containing block for `position: fixed` descendants
 * (transform, will-change: transform, contain, or perspective).
 * @param {HTMLElement} el element to test
 * @returns {boolean} true if it establishes a containing block
 */
const establishesContainingBlock = (el: HTMLElement): boolean => {
  const {
    transform, willChange, contain, perspective
  } = getComputedStyle(el);
  return (!!transform && transform !== 'none')
    || (!!willChange && willChange.includes('transform'))
    || (!!contain && contain !== 'none' && contain !== 'style')
    || (!!perspective && perspective !== 'none');
};

/**
 * Finds the nearest ancestor that establishes a containing block for fixed positioning,
 * crossing shadow DOM boundaries. When inside an ids-modal, its popup container is used.
 * @param {IdsDataGridCell} [cell] the editing cell
 * @returns {HTMLElement | null} the containing-block ancestor, or null
 */
const findContainingBlockAncestor = (cell?: IdsDataGridCell): HTMLElement | null => {
  const modalContainer = (cell?.dataGrid?.closest('ids-modal')
    ?.shadowRoot?.querySelector('ids-popup') as any)?.container as HTMLElement | undefined;
  if (modalContainer) return modalContainer;

  let node: Node | null = cell?.dataGrid ?? null;
  while (node && node !== document.body) {
    const parent = (node as HTMLElement).parentElement;
    if (parent) {
      if (establishesContainingBlock(parent)) return parent;
      node = parent;
    } else {
      const root = (node as HTMLElement).getRootNode?.();
      node = (root instanceof ShadowRoot) ? root.host : null;
    }
  }
  return null;
};

export class InputEditor implements IdsDataGridEditor {
  /** The type of editor (i.e. input, dropdown, checkbox ect) */
  type = 'input';

  /** Holds the Editor */
  input?: IdsInput;

  /**
   * Create an input and set the value and focus states
   * @param {IdsDataGridCell} cell the cell element
   */
  init(cell?: IdsDataGridCell) {
    const isInline = cell?.column.editor?.inline;
    this.input = <IdsInput>document.createElement('ids-input');
    this.input.colorVariant = isInline ? 'in-cell' : 'borderless';
    this.input.size = 'full';
    this.input.fieldHeight = String(cell?.dataGrid?.rowHeight) === 'xxs' ? `xs` : String(cell?.dataGrid?.rowHeight);
    this.input.labelState = 'collapsed';

    // Clear cell and set value
    // Use cell.value instead of innerText to preserve whitespace characters like \n
    const value = cell?.value;
    cell!.innerHTML = '';
    cell?.appendChild(this.input as any);
    this.input.addEventListener('keydown', (e) => applyEditorEndCellEdit(cell!, e));

    this.input.value = value;

    if (this.input instanceof IdsInput && cell) {
      if (!isInline) this.input.shadowRoot?.querySelector('input')?.style.setProperty('width', `${cell.offsetWidth - 5}px`);
      applyEditorSettings(this.input, cell?.column.editor?.editorSettings);
      applyEditorValidation(this.input, cell?.column.editor?.editorValidation);
    }
    this.input.shadowRoot?.querySelector('input')?.focus({ preventScroll: true });
  }

  /* Transform the value */
  save() {
    return { value: this.input?.value };
  }

  /* Destroy the editor */
  destroy() {
    this.input = undefined;
  }

  value() {
    return this.input?.value ?? '';
  }

  change(newValue: boolean | number | string) {
    if (this.input) this.input.value = String(newValue);
  }
}

/**
 * Password Input Editor for IDS Data Grid
 * Shows masked input with revealed last characters and toggle button
 */
export class PasswordInputEditor extends InputEditor {
  type = 'password';

  #toggleButton?: IdsIcon;

  /**
   * Create a password input and set the value and focus states
   * @param {IdsDataGridCell} cell the cell element
   */
  init(cell?: IdsDataGridCell) {
    super.init(cell);
    const dataIndex = getClosest(cell, 'ids-data-grid-row').getAttribute('data-index');
    const rowData = getClosest(cell, 'ids-data-grid').data[dataIndex];
    const isInline = cell?.column.editor?.inline;

    // Make the input password type
    this.input!.type = 'password';

    // Create toggle button
    this.#toggleButton = <IdsIcon>document.createElement('ids-icon');
    this.#toggleButton.icon = 'show-item';
    this.#toggleButton.classList.add('editor-cell-icon');
    this.#toggleButton.classList.add('text-mask');

    // Clear cell and set value
    const value = rowData[cell!.column.id];
    cell!.innerHTML = '';

    // Add input and button to container
    cell?.appendChild(this.input as any);
    cell?.appendChild(this.#toggleButton as any);

    // set value to the input
    this.input!.value = value;

    // Handle toggle button click
    this.#toggleButton.offEvent('click');
    this.#toggleButton.onEvent('click', this.#toggleButton, () => {
      if (!this.input || !this.#toggleButton) {
        return;
      }

      const masked = this.input.type === 'password';

      const inputEl = this.input.shadowRoot?.querySelector('input');

      // Store current cursor position
      let cursorPos = 0;
      if (inputEl) {
        cursorPos = inputEl.selectionStart || 0;
      }

      if (masked) {
        this.input.type = 'text';
        this.#toggleButton.icon = 'hide-item';
      } else {
        this.#toggleButton.icon = 'show-item';
        this.input.type = 'password';
      }

      this.input.focus();

      // Restore cursor position
      if (inputEl) {
        setTimeout(() => {
          inputEl.setSelectionRange(cursorPos, cursorPos);
        }, 0);
      }
    });

    if (this.input instanceof IdsInput && cell) {
      if (!isInline) this.input.shadowRoot?.querySelector('input')?.style.setProperty('width', `100%`);
      applyEditorSettings(this.input, cell?.column.editor?.editorSettings);
      applyEditorValidation(this.input, cell?.column.editor?.editorValidation);
    }
    this.input!.focus();
  }

  /**
   * Transform the value for saving
   * @returns {IdsDataGridSaveValue} The value to save
   */
  save() {
    return { value: this.input?.value };
  }

  /**
   * Destroy the editor
   */
  destroy() {
    this.input?.removeEventListener('input', () => { });
    this.#toggleButton?.removeEventListener('click', () => { });
    this.input = undefined;
    this.#toggleButton = undefined;
  }
}

export class CheckboxEditor implements IdsDataGridEditor {
  /** The type of editor (i.e. input, dropdown, checkbox ect) */
  type = 'checkbox';

  /** Holds the Editor */
  input?: IdsCheckbox;

  /** MouseEvent if click was used to edit */
  clickEvent?: MouseEvent;

  /** Cache original value type for proper saving */
  #originalValue?: any;

  /** Cache custom functions from editorSettings */
  #customSave?: (value: any) => void;

  #customValidate?: (value: any) => void;

  /**
   * Create an input and set the value and focus states
   * @param {IdsDataGridCell} cell the cell element
   */
  init(cell?: IdsDataGridCell) {
    this.input = <IdsCheckbox>document.createElement('ids-checkbox');

    const row = getClosest(cell, 'ids-data-grid-row');
    const dataIndex = row?.getAttribute('data-index');
    const grid = getClosest(cell, 'ids-data-grid');
    const rowData = dataIndex !== null && grid?.data ? grid.data[dataIndex] : undefined;
    this.#originalValue = rowData && cell ? rowData[cell.column.id] : undefined;

    const settings = cell?.column.editor?.editorSettings;
    this.#customSave = typeof settings?.customSave === 'function' ? settings.customSave as (value: any) => void : undefined;
    this.#customValidate = typeof settings?.customValidate === 'function' ? settings.customValidate as (value: any) => void : undefined;

    // Clear cell and set value
    const value = stringToBool(cell?.querySelector('[aria-checked]')?.getAttribute('aria-checked'));
    cell!.innerHTML = '';
    if (!this.clickEvent) this.input.noAnimation = true;
    this.input.checked = this.clickEvent ? !value : value;
    (this.input as any).isDirty = !!this.clickEvent;

    applyEditorSettings(this.input, settings);

    cell?.appendChild(this.input as any);

    requestAnimationFrame(() => {
      cell?.endCellEdit();
      cell?.focus();
    });
  }

  /* Transform the value */
  save() {
    const checked = this.input?.checked;
    let value: any = checked;

    if (typeof this.#originalValue === 'number') {
      value = checked ? 1 : 0;
    } else if (typeof this.#originalValue === 'string') {
      value = checked ? 'true' : 'false';
    }
    if (this.#customValidate) this.#customValidate(value);
    if (this.#customSave) this.#customSave(value);

    return { value };
  }

  /* Destroy the editor */
  destroy() {
    this.input?.offEvent('keydown');
    this.input = undefined;
    this.#originalValue = undefined;
    this.#customSave = undefined;
    this.#customValidate = undefined;
  }

  value() {
    return !!this.input?.checked;
  }

  change(newValue: boolean | number | string) {
    if (this.input) this.input.checked = Boolean(newValue);
  }
}

export class DropdownEditor implements IdsDataGridEditor {
  /** The type of editor (i.e. input, dropdown, checkbox ect) */
  type = 'dropdown';

  /** Holds the Editor */
  input?: IdsDropdown;

  /** Holds the separate Dropdown List */
  list?: IdsDropdownList;

  /** Cache cell reference */
  #cell?: IdsDataGridCell;

  /** Cache dropdown value */
  #value?: string | null;

  /** Callback reference to handle blur event propagation */
  #stopPropagationCb = this.stopPropagation.bind(this);

  /** Resize handler reference for cleanup */
  #resizeHandler?: (() => void) | null = null;

  /** MouseEvent if click was used to edit */
  clickEvent?: MouseEvent;

  async init(cell?: IdsDataGridCell): Promise<void> {
    this.#value = cell?.querySelector('[data-value]')?.getAttribute('data-value') ?? cell?.value ?? null;
    const isInline = cell?.column.editor?.inline;
    const settings = { ...cell?.column?.editor?.editorSettings };
    const validation = { ...cell?.column?.editor?.editorValidation };
    let settingsOptions = <any[]>settings?.options ?? [];

    this.input = <IdsDropdown>document.createElement('ids-dropdown');
    this.list = <IdsDropdownList>document.createElement('ids-dropdown-list');

    // Set combobox if specified in settings
    if (cell?.column?.editor?.editorSettings?.comboBox) {
      this.input.combobox = true;
    }

    if (cell?.column?.editor?.editorSettings?.source !== undefined
      && cell?.column?.editor?.editorSettings?.source instanceof Function) {
      cell?.column?.editor?.editorSettings?.source().then(async (result: unknown) => {
        const newOptions = await result;

        if (!Array.isArray(newOptions) || !newOptions.length) {
          console.error('[DropdownEditor] source() must return an array of options');
          return;
        }

        settings.options = newOptions;
        settingsOptions = newOptions as unknown[];

        if (!this.input) {
          return;
        }

        this.input.loadDataSet(settingsOptions);
        const nativeInput = this.input.input?.input;
        const matchedOption = this.input.options.find(
          (opt: IdsListBoxOption) => opt.value === this.#value
        );
        if (nativeInput && matchedOption) {
          nativeInput.value = matchedOption.textContent?.trim() ?? this.#value ?? '';
          this.input.selectOption(matchedOption, true);
        }

        cell.syncEditorStateAfterLoad(cell.column, this.type, true);
      }).catch((err: unknown) => {
        console.error('[IdsDataGrid] Failed to load dropdown source options:', err);
      });
    }

    // apply user settings
    delete settings.options;
    applyEditorSettings(this.input, settings);
    if (Boolean(this.input.typeahead) === true) {
      this.input.typeahead = false;
    }

    cell!.innerHTML = '';
    cell!.appendChild(this.input);
    this.input.addEventListener('keydown', (e) => applyEditorEndCellEdit(cell!, e));

    cell!.appendChild(this.list);
    cell!.classList.add('is-focused');
    this.#cell = cell!;

    this.list.insertAdjacentHTML('beforeend', '<ids-list-box></ids-list-box>');
    this.list.setAttribute('trigger-type', 'custom');
    this.input.setAttribute('id', 'ids-dropdown-active-cell');
    this.list.setAttribute('id', 'ids-dropdown-list-active-cell');
    this.input.setAttribute('list', '#ids-dropdown-list-active-cell');

    const optionValues = settingsOptions.map((row) => String(row.value).toLowerCase());
    const defaultValue = cell?.defaultValue || this.#value;

    const isMultiColumn = cell?.column?.editor?.editorSettings?.multiColumn;

    // Don't add default value option for multicolumn dropdowns
    if (defaultValue && !optionValues.includes(defaultValue.toLowerCase()) && !isMultiColumn) {
      const [firstOption, ...remainingOptions] = settingsOptions;
      const defaultValueOption = { id: defaultValue, label: defaultValue, value: defaultValue };
      if (this.input.comboBox) {
        settingsOptions = [firstOption, ...remainingOptions];
      } else if (firstOption || (remainingOptions && remainingOptions.length > 0)) {
        settingsOptions = [firstOption, defaultValueOption, ...remainingOptions];
      } else {
        settingsOptions = [defaultValueOption];
      }
    }

    this.input.loadDataSet(settingsOptions.filter((item) => (item === 0 || Boolean(item))));

    this.list.configurePopup();
    this.list.setAttribute('slot', 'menu-container');
    this.list.setAttribute('size', 'full');
    this.list.setAttribute('attachment', '.ids-data-grid-wrapper');
    this.list.appendToTargetParent();

    if (this.list?.popup) {
      this.list.popup.alignTarget = this.input;

      const splitterPane = cell?.dataGrid?.closest('ids-splitter-pane');
      const splitter = splitterPane?.closest('ids-splitter') as HTMLElement | null;

      // When the grid is nested in a scroll/overflow container (splitter pane or accordion),
      // an absolutely-positioned popup is clipped by that ancestor. Fixed positioning lets
      // the popup escape the clipping boundary and render its full option list.
      const clippingAncestor = cell?.dataGrid?.closest('ids-accordion, div');
      const useFixed = !!splitterPane || !!clippingAncestor;
      // Inside an accordion the popup is positioned relative to the viewport, so the
      // available space for the option list is the viewport, not the (often short) grid
      // wrapper. Using the wrapper here would wrongly flip the popup above the cell and
      // clip its top. Splitter panes keep their own boundary.
      const useViewportBoundary = !!clippingAncestor && !splitterPane;

      // A fixed-position popup is resolved against the nearest ancestor that establishes
      // a containing block (transform, will-change, contain, or perspective) instead of the
      // viewport. Find that ancestor so we can offset our coordinates to compensate.
      const containingBlockAncestor = findContainingBlockAncestor(cell);

      if (useFixed) {
        this.list.popup.positionStyle = 'fixed';
      }

      this.list.popup.onPlace = (_popupRect: DOMRect, domElement) => {
        if (splitter && splitterPane) {
          const paneBounds = splitterPane.getBoundingClientRect();

          if (_popupRect.width > paneBounds.width) {
            this.list!.popup!.containingElem = splitter;
          } else {
            this.list!.popup!.containingElem = splitterPane as IdsSplitterPane;
          }
        }

        domElement.style.visibility = 'hidden';
        return _popupRect;
      };

      this.list.popup.onPlaceEnd = (popupRect: DOMRect, domElement) => {
        domElement.style.visibility = '';

        // Align dropdown popup top to cell bottom for all row heights
        const popupBoundary = this.list!.popup!.containingElem === splitter
          ? splitter
          : splitterPane || cell?.dataGrid?.container?.querySelector('.ids-data-grid-wrapper') || cell?.dataGrid?.container;

        if (!popupBoundary) {
          return popupRect;
        }

        const boundaryBounds = popupBoundary.getBoundingClientRect();
        const cellRect = cell?.getBoundingClientRect();
        if (cellRect && boundaryBounds) {
          const containingBlockRect = containingBlockAncestor?.getBoundingClientRect();
          const offsetParentRect = !useFixed ? domElement.offsetParent?.getBoundingClientRect() : null;
          const containerTop = containingBlockRect?.top ?? offsetParentRect?.top ?? 0;

          const listBoxScrollHeight = this.list?.listBox?.scrollHeight || 0;
          const measuredHeight = domElement.getBoundingClientRect().height || popupRect.height;
          const popupHeight = Math.max(listBoxScrollHeight, measuredHeight);

          const viewportHeight = window.innerHeight;
          const edgeGap = 8;
          let boundaryBottom = useViewportBoundary
            ? viewportHeight
            : Math.min(viewportHeight, boundaryBounds.bottom);
          if (containingBlockRect) {
            boundaryBottom = Math.min(boundaryBottom, containingBlockRect.bottom);
          }
          boundaryBottom -= edgeGap;
          const boundaryTop = (containingBlockRect?.top ?? 0) + edgeGap;

          const spaceBelow = boundaryBottom - cellRect.bottom;
          const spaceAbove = cellRect.top - boundaryTop;
          const minHeight = 100;

          const shouldFlipAbove = popupHeight > spaceBelow
            && spaceAbove > spaceBelow
            && spaceAbove >= minHeight;
          const popupContainer = this.list?.popup?.container;

          if (shouldFlipAbove) {
            const finalHeight = Math.min(popupHeight, spaceAbove);
            domElement.style.top = `${(cellRect.top - containerTop) - finalHeight}px`;
            popupContainer?.classList.add('flipped');
            if (popupHeight > spaceAbove) {
              domElement.style.height = `${spaceAbove}px`;
              this.list?.listBox?.classList.add('height-adjusted');
            }
          } else {
            domElement.style.top = `${cellRect.bottom - containerTop}px`;
            popupContainer?.classList.remove('flipped');
            if (popupHeight > spaceBelow) {
              domElement.style.height = `${Math.max(spaceBelow, minHeight)}px`;
              this.list?.listBox?.classList.add('height-adjusted');
            }
          }
        }

        // Get the current x position of cell
        const cellLeft = cell?.getBoundingClientRect().left || 0;
        const containingBlockLeft = containingBlockAncestor?.getBoundingClientRect().left;
        const containerLeft = containingBlockLeft
          ?? (useFixed ? 0 : domElement.offsetParent?.getBoundingClientRect().left || 0);

        domElement.style.left = `${cellLeft - containerLeft}px`;

        const autoSize = cell?.column?.editor?.editorSettings?.autoSize;
        const inputAutoSize = cell?.column?.editor?.editorSettings?.inputAutoSize;
        const cellWidth = cell?.getBoundingClientRect().width || 0;

        if (autoSize && cellWidth > 0) {
          domElement.style.minWidth = `${cellWidth}px`;
          if (inputAutoSize !== false) {
            domElement.style.width = `${cellWidth}px`;
          }
        } else if (cellWidth > 0) {
          // The popup is positioned flush to the cell's left edge, so match its width to the
          // cell width to keep the right edge aligned with the column border (avoids a 1-2px gap).
          domElement.style.width = `${cellWidth}px`;
          domElement.style.maxWidth = `${cellWidth}px`;
        }

        if (domElement) {
          const containerBounds = popupBoundary.getBoundingClientRect();
          const dropdownBounds = domElement.getBoundingClientRect();
          const currentPosition = parseInt(domElement.style.left) || 0;

          if (dropdownBounds.right > containerBounds.right) {
            const rightOverflow = dropdownBounds.right - containerBounds.right;
            domElement.style.left = `${currentPosition - rightOverflow}px`;
          } else if (dropdownBounds.left < containerBounds.left) {
            const leftOverflow = containerBounds.left - dropdownBounds.left;
            domElement.style.left = `${currentPosition + leftOverflow}px`;
          }
        }

        return popupRect;
      };
    }

    this.input.allowBlank = !!settings.allowBlank;
    this.input.value = this.#value ?? '';
    this.input.size = 'full';
    this.input.labelState = 'collapsed';
    this.input.colorVariant = isInline ? 'in-cell' : 'borderless';
    this.input.fieldHeight = String(cell?.dataGrid?.rowHeight) === 'xxs' ? 'xs' : String(cell?.dataGrid?.rowHeight);
    const triggerField = this.input.container?.querySelector<IdsTriggerField>('ids-trigger-field');
    if (triggerField) {
      triggerField.focus();
      triggerField.colorVariant = isInline ? 'in-cell' : 'borderless';
    }

    if (this.input.comboBox) {
      this.input.dropdownList?.classList.add('is-combobox');
      triggerField?.setAttribute('combobox', 'true');
      triggerField?.setAttribute('autoselect', 'true');
      triggerField?.classList.add('combo-filter');
      if (triggerField?.input) triggerField.input.value = this.input.value;
    }

    // apply user validation
    if (validation) {
      applyEditorValidation(this.input, validation);
    }

    this.#attachEventListeners();

    // Only open if initiated by a click
    const target = this.clickEvent?.target as HTMLElement;
    const isTriggerClick = target?.tagName === 'IDS-ICON' || target?.closest('ids-icon');
    this.input.afterShow = () => {
      cell?.syncEditorStateAfterLoad(cell?.column, this.type, true);
    };
    if (this.clickEvent && (!this.input.comboBox || isTriggerClick)) {
      await this.input?.open(Boolean(this.input.typeahead));
    } else {
      // Focus the appropriate element based on dropdown type
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      applyEditorDropdownFocus(this);
    }
  }

  /**
   * Overrides data grid cell's focusout event handling
   * @param {FocusEvent} evt focus event
   */
  stopPropagation(evt: FocusEvent) {
    const relatedTarget = evt.relatedTarget as HTMLElement | null;
    if (!relatedTarget) return;

    const tagName = relatedTarget.tagName;
    const isOwnDropdown = tagName === 'IDS-DROPDOWN' && relatedTarget === this.input;
    const isOwnListOption = tagName === 'IDS-LIST-BOX-OPTION' && (this.list?.contains(relatedTarget) ?? false);

    if (isOwnDropdown || isOwnListOption) {
      evt.stopPropagation();
      evt.stopImmediatePropagation();
    }
  }

  /**
   * Attach dropdown event handlers
   */
  #attachEventListeners() {
    this.input?.onEvent('change', this.input, (evt) => {
      this.#value = (this.input?.allowBlank && evt.detail.value === 'blank') ? '' : evt.detail.value;
    });
    this.input?.onEvent('selected', this.input, (evt) => {
      this.#value = (this.input?.allowBlank && evt.detail.value === 'blank') ? '' : evt.detail.value;
    });
    this.input?.onEvent('focusout', this.input, this.#stopPropagationCb);
    if (this.list) {
      this.list.onOutsideClick = (e: MouseEvent) => {
        if (!e.composedPath().includes(this.list!)) {
          this.input?.close();
        }
      };
      this.list.refreshTriggerEvents();
      // Close code is in #startCellEdit
    }

    this.input?.onEvent('keydown', this.list, (e) => {
      const key = e.key;
      if (key === 'Enter') {
        e.stopPropagation();
      }
    });

    // Reposition dropdown on window resize (zoom change) to prevent misalignment
    this.#resizeHandler = () => {
      if (this.list?.popup?.visible) {
        this.list.popup.place();
      }
    };
    window.addEventListener('resize', this.#resizeHandler);
    this.input?.onEvent('close', this.input, () => {
      if (this.#resizeHandler) {
        window.removeEventListener('resize', this.#resizeHandler);
      }
    });
  }

  /* Save selected dropdown value */
  save() {
    let value: string | null | undefined = this.input?.input?.value;
    let dirtyCheckValue: string | null | undefined = this.input?.input?.value;

    // Handle multicolumn dropdown with showValue setting
    const isMultiColumn = this.#cell?.column?.editor?.editorSettings?.multiColumn;
    const hasShowValue = this.#cell?.column?.editor?.editorSettings?.showValue !== undefined;
    if (isMultiColumn && hasShowValue) {
      const showValue = this.#cell?.column?.editor?.editorSettings?.showValue;
      const selectedOption = this.input?.selectedOption as IdsListBoxOption;
      if (selectedOption) {
        value = showValue ? selectedOption.value : selectedOption.label;
        dirtyCheckValue = value;
      }
    }

    if (this.input?.allowBlank && value === 'blank') {
      value = '';
      dirtyCheckValue = '';
    }

    return {
      value,
      dirtyCheckValue
    };
  }

  /**
   * Destroy dropdown editor
   */
  destroy(): void {
    if (this.#resizeHandler) {
      window.removeEventListener('resize', this.#resizeHandler);
      this.#resizeHandler = null;
    }
    this.input?.offEvent('click');
    this.input?.offEvent('change');
    this.input?.offEvent('focusout', this.input, this.#stopPropagationCb);
    this.list?.offEvent('keydown');
    this.list?.remove();
    this.#value = undefined;

    this.#cell?.offEvent('click');
    this.#cell?.offEvent('click.dropdown-toggle');
    this.#cell?.classList?.remove('is-focused');
    this.#cell = undefined;
  }

  value() {
    return this.input?.value ?? '';
  }

  change(newValue: boolean | number | string) {
    if (this.input) this.input.value = String(newValue);
  }
}

export class DatePickerEditor implements IdsDataGridEditor {
  public type = 'datepicker';

  public input?: IdsTriggerField;

  public popup?: IdsDatePickerPopup;

  clickEvent?: MouseEvent;

  #value?: Date;

  #displayValue = '';

  init(cell?: IdsDataGridCell) {
    this.input = this.#buildDatePickerTriggerField(cell!);
    this.popup = this.#buildDatePickerPopup(cell!);

    // parse date string
    this.#update(cell!, cell!.originalValue as string);

    // insert datepicker component and focus
    cell!.innerHTML = '';
    cell!.appendChild(this.popup);
    cell!.appendChild(this.input);
    this.input.addEventListener('keydown', (e) => applyEditorEndCellEdit(cell!, e));

    this.input.value = this.#displayValue;
    this.popup?.syncDateAttributes(this.#value ?? new Date());
    if (this.input) {
      const autoselect = cell?.column?.editor?.editorSettings?.autoselect;
      this.input.autoselect = Boolean(autoselect !== undefined ? autoselect : true);
    }

    const validation = cell?.column.editor?.editorValidation;
    if (validation) {
      applyEditorValidation(this.input, validation);
    }

    if (this.popup) {
      const popup = this.popup;
      this.popup.value = this.#displayValue;
      this.popup.attachment = '.ids-data-grid-wrapper';
      this.popup.slot = 'menu-container';

      this.popup.appendToTargetParent();
      if (this.popup.popup) {
        this.popup.popup.positionStyle = 'fixed';
        this.popup.popup.popupOpenEventsTarget = document.body;
        this.popup.popup.onOutsideClick = (e: MouseEvent) => {
          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          if (!e.composedPath().includes(popup)) { popup.hide(); }
        };
      }

      // apply popup required settings
      this.popup.id = `${cell!.column.field}-date-picker-popup`;
      this.popup.triggerType = 'click';
      this.popup.triggerElem = `#${cell!.column.field}-date-picker-btn`;
      this.popup.target = `#${cell!.column.field}-date-picker`;
      this.popup.popup!.align = 'bottom, right';
      this.popup.popup!.arrowTarget = `#${cell!.column.field}-date-picker-btn`;
      this.popup.popup!.y = 16;
      this.popup.refreshTriggerEvents();

      // Show button group
      if (this.popup.hasTime()) {
        this.popup.showApply = true;
        const buttonGroup = this.popup.container?.querySelector('.popup-footer');
        buttonGroup?.classList.remove('hidden');
      }

      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      applyEditorPopupFocus(this);
    }

    this.#attachEventListeners(cell);
  }

  #buildDatePickerTriggerField(cell: IdsDataGridCell): IdsTriggerField {
    const component = <IdsTriggerField>document.createElement('ids-trigger-field');

    // apply user settings
    applyEditorSettings(component, cell?.column.editor?.editorSettings);

    component.id = `${cell.column.field}-date-picker`;
    component.fieldHeight = String(cell?.dataGrid?.rowHeight) === 'xxs' ? `xs` : String(cell?.dataGrid?.rowHeight);
    component.labelState = 'collapsed';
    component.colorVariant = 'borderless';
    component.size = 'full';
    component.mask = true;

    component.insertAdjacentHTML(
      'beforeend',
      `<ids-trigger-button
        class="ids-trigger-field-slot-trigger-end"
        id="${cell.column.field}-date-picker-btn"
        slot="trigger-end"
        icon="calendar"></ids-trigger-button>`
    );

    return component;
  }

  #buildDatePickerPopup(cell: IdsDataGridCell): IdsDatePickerPopup {
    const component = <IdsDatePickerPopup>document.createElement('ids-date-picker-popup');
    // apply user settings
    applyEditorSettings(component, cell?.column.editor?.editorSettings);
    return component;
  }

  #update(cell: IdsDataGridCell, dateString: string) {
    const inputDate = cell?.dataGrid.localeAPI.parseDate(
      dateString,
      { dateFormat: cell?.column?.editor?.editorSettings?.format || undefined }
    ) as Date;

    if (!dateString || !isValidDate(inputDate)) {
      this.#displayValue = dateString;
      this.#value = undefined;
      return;
    }

    // update original date value, preserving time from input if available
    if (this.#value instanceof Date) {
      this.#value.setFullYear(inputDate.getFullYear());
      this.#value.setMonth(inputDate.getMonth());
      this.#value.setDate(inputDate.getDate());
      // Update time components if they differ from default (00:00:00)
      if (inputDate.getHours() !== 0 || inputDate.getMinutes() !== 0
        || inputDate.getSeconds() !== 0 || inputDate.getMilliseconds() !== 0) {
        this.#value.setHours(inputDate.getHours());
        this.#value.setMinutes(inputDate.getMinutes());
        this.#value.setSeconds(inputDate.getSeconds());
        this.#value.setMilliseconds(inputDate.getMilliseconds());
      }
    } else {
      this.#value = inputDate;
    }

    this.#displayValue = cell!.dataGrid.localeAPI.formatDate(this.#value, cell.column?.editor?.editorSettings);
  }

  #stopPropagation(evt: FocusEvent): void {
    if (!this.popup?.visible) return;

    evt.stopImmediatePropagation();
    evt.stopPropagation();
  }

  #attachEventListeners(cell?: IdsDataGridCell | undefined) {
    const focusOutHandler = (evt: FocusEvent) => this.#stopPropagation(evt);
    this.input?.onEvent('focusout', this.input, focusOutHandler, { capture: true });
    this.popup?.onEvent('focusout', this.popup, focusOutHandler, { capture: true });

    const keydownHandler = async (e: KeyboardEvent) => {
      if (!this.popup?.visible) return;

      if (e.key === 'Escape') {
        e.stopPropagation();
        await this.popup?.hide();
      } else if (['Tab', 'Enter', ' '].includes(e.key)) {
        e.stopPropagation();
      }
    };

    this.input?.onEvent('keydown', this.input, keydownHandler);
    this.popup?.onEvent('keydown', this.popup, keydownHandler);

    this.popup?.onEvent('hide', this.popup, () => {
      cell?.dataGrid?.offEvent('keydown.datepicker-editor');
      if (cell!.contains(cell!.dataGrid!.shadowRoot!.activeElement)) return;
      if (!cell!.dataGrid!.contains(document.activeElement)) return;
      if (!cell!.dataGrid!.shadowRoot!.contains(document.activeElement)) return;

      cell?.focus();
    });
  }

  save(cell?: IdsDataGridCell) {
    this.#update(cell!, cell?.value);
    // When saveFormattedValue is true, skip ISO conversion so the display value is used.
    // When false (default), attempt ISO 8601 string (e.g., "2025-01-15T00:00:00.000Z").
    // Falls back to displayValue when input is not a valid date (preserves free-text input).
    const dateValue = cell?.column.editor?.editorSettings?.saveFormattedValue
      ? '' : this.#value?.toISOString();
    return {
      value: (dateValue || this.#displayValue) ?? '',
      dirtyCheckValue: this.#displayValue
    };
  }

  destroy() {
    this.input?.offEvent('focusout');
    this.input?.offEvent('keydown');
    this.input?.offEvent('outsideclick.datepicker');
    this.popup?.offEvent('focusout');
    this.popup?.offEvent('keydown');
    this.popup?.offEvent('hide');
    this.popup?.remove();

    this.#value = undefined;
    this.#displayValue = '';
  }

  value() {
    return this.input?.input?.value ?? '';
  }

  change(newValue: boolean | number | string) {
    if (this.input?.input) this.input.input.value = String(newValue);
  }
}

export class TimePickerEditor implements IdsDataGridEditor {
  type = 'timepicker';

  input?: IdsTriggerField;

  popup?: IdsTimePickerPopup;

  clickEvent?: MouseEvent;

  #originalDate?: Date;

  init(cell?: IdsDataGridCell | undefined) {
    this.input = this.#buildTimePickerTriggerField(cell!);
    this.popup = this.#buildTimePickerPopup(cell!);

    const localeTimeFormat = cell!.dataGrid!.localeAPI!.calendar().timeFormat;
    const settingsFormat = cell?.column?.editor?.editorSettings?.format;
    const isCustomFormat = settingsFormat && settingsFormat !== localeTimeFormat
      && settingsFormat !== 'h:mm a' && settingsFormat !== 'hh:mm a';

    const format = isCustomFormat ? settingsFormat : (localeTimeFormat || 'h:mm a');
    this.input.format = format;
    this.popup.format = format;

    const displayPattern = (cell?.column?.formatOptions as Record<string, string>)?.format || format;
    const dateString = cell!.originalValue as string ?? '';
    const date = cell!.dataGrid!.localeAPI!.parseDate(dateString, { pattern: displayPattern }, true) as Date;
    const isValid = isValidDate(date);
    this.#originalDate = isValid ? date : undefined;
    this.input.value = isValid
      ? cell!.dataGrid!.localeAPI!.formatDate(this.#originalDate, { pattern: this.input.format })
      : dateString;

    // insert time picker and focus
    cell!.innerHTML = '';
    cell!.appendChild(this.popup);
    cell!.appendChild(this.input);
    this.input.addEventListener('keydown', (e) => applyEditorEndCellEdit(cell!, e));

    if (this.input) {
      const autoselect = cell?.column?.editor?.editorSettings?.autoselect;
      this.input.autoselect = Boolean(autoselect !== undefined ? autoselect : true);
    }

    if (this.popup) {
      const popup = this.popup;
      this.popup.attachment = '.ids-data-grid-wrapper';
      this.popup.slot = 'menu-container';

      this.popup.appendToTargetParent();
      if (this.popup.popup) {
        this.popup.popup.positionStyle = 'fixed';
        this.popup.popup.popupOpenEventsTarget = document.body;
        this.popup.popup.onOutsideClick = async (e: MouseEvent) => {
          if (!e.composedPath().includes(popup)) { await popup.hide(); }
        };
      }

      // apply popup required settings
      this.popup.id = `${cell!.column.field}-time-picker-popup`;
      this.popup.triggerType = 'click';
      this.popup.triggerElem = `#${cell!.column.field}-time-picker-btn`;
      this.popup.target = `#${cell!.column.field}-time-picker`;
      this.popup.popup!.align = 'bottom, right';
      this.popup.popup!.arrowTarget = `#${cell!.column.field}-time-picker-btn`;
      this.popup.popup!.y = 16;
      this.popup.refreshTriggerEvents();

      if (this.#originalDate) {
        const hours = this.#originalDate.getHours();
        const is12Hr = format.includes('h');
        if (is12Hr) {
          this.popup.hours = hoursTo12(hours);
          this.popup.period = hours >= 12 ? 'PM' : 'AM';
        } else {
          this.popup.hours = hours;
        }
        this.popup.minutes = this.#originalDate.getMinutes();
        this.popup.seconds = this.#originalDate.getSeconds();
      } else {
        // Default to current time when cell has no value
        const now = new Date();
        const hours = now.getHours();
        const is12Hr = format.includes('h');

        if (is12Hr) {
          this.popup.hours = hoursTo12(hours);
          this.popup.period = hours >= 12 ? 'PM' : 'AM';
        } else {
          this.popup.hours = hours;
        }
        this.popup.minutes = now.getMinutes();
      }

      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      applyEditorPopupFocus(this);
    }

    this.#attachEventListeners(cell);
  }

  #buildTimePickerTriggerField(cell: IdsDataGridCell): IdsTriggerField {
    const component = <IdsTriggerField>document.createElement('ids-trigger-field');

    // apply user settings
    applyEditorSettings(component, cell?.column.editor?.editorSettings);

    // apply required settings
    component.id = `${cell.column.field}-time-picker`;
    component.fieldHeight = String(cell?.dataGrid?.rowHeight) === 'xxs' ? `xs` : String(cell?.dataGrid?.rowHeight);
    component.labelState = 'collapsed';
    component.colorVariant = 'borderless';
    component.size = 'full';
    component.mask = true;

    component.insertAdjacentHTML(
      'beforeend',
      `<ids-trigger-button
        class="ids-trigger-field-slot-trigger-end"
        id="${cell.column.field}-time-picker-btn"
        slot="trigger-end"
        icon="clock"></ids-trigger-button>`
    );

    return component;
  }

  #buildTimePickerPopup(cell: IdsDataGridCell): IdsTimePickerPopup {
    const popup = <IdsTimePickerPopup>document.createElement('ids-time-picker-popup');

    // apply user settings
    applyEditorSettings(popup, cell?.column.editor?.editorSettings);

    return popup;
  }

  #attachEventListeners(cell?: IdsDataGridCell | undefined) {
    this.popup?.onEvent('hide', this.popup, () => {
      if (cell!.contains(cell!.dataGrid!.shadowRoot!.activeElement)) return;
      cell?.focus();
    });
  }

  save(cell?: IdsDataGridCell) {
    let date;
    const inputValue = this.input!.value;
    if (inputValue) {
      date = cell!.dataGrid.localeAPI!.parseDate(inputValue, { pattern: this.input!.format }, true) as Date;

      // Timepicker formats don't include the "date" portion,
      // So this fills in the missing parts from the `#originalDate` if possible to save a valid date value.
      if (this.#originalDate && (
        this.#originalDate.getMonth() !== date?.getMonth()
        || this.#originalDate.getFullYear() !== date?.getFullYear()
        || this.#originalDate.getDate() !== date?.getDate()
      )) {
        date?.setMonth(this.#originalDate.getMonth());
        date?.setFullYear(this.#originalDate.getFullYear());
        date?.setDate(this.#originalDate.getDate());
      }
    }

    return {
      value: date && isValidDate(date) && !cell?.column.editor?.editorSettings?.saveFormattedValue ? date?.toISOString() : inputValue ?? '',
      dirtyCheckValue: inputValue
    };
  }

  destroy() {
    this.input?.offEvent('focusout');
    this.input?.detachAllListeners();
    this.input?.remove();
    this.popup?.offEvent('hide');
    this.popup?.detachAllListeners();
    this.popup?.remove();
    this.#originalDate = undefined;
  }

  value() {
    return this.input?.value ?? '';
  }

  change(newValue: boolean | number | string) {
    if (this.input) this.input.value = String(newValue);
  }
}

export class TreeEditor extends InputEditor {
  expandButton?: IdsButton;

  fieldContainer?: HTMLElement;

  /**
   * Adds extra button element for mimicking the tree formatter's display
   * @param {IdsDataGridCell} cell the cell element
   */
  init(cell?: IdsDataGridCell) {
    super.init(cell);

    const buttonHTML = this.#isExpandable(cell!) ? `<ids-button class="expand-button" disabled="true">
      <ids-icon icon="plusminus-folder-${this.#isExpanded(cell!) ? 'open' : 'closed'}"></ids-icon>
    </ids-button>` : '';

    cell!.insertAdjacentHTML('afterbegin', `<span class="ids-data-grid-tree-container">
      ${buttonHTML}
      <span class="ids-data-grid-tree-field-container"></span>
    </span>`);

    this.expandButton = cell!.querySelector<IdsButton>('.expand-button')!;
    this.fieldContainer = cell!.querySelector<HTMLElement>('.ids-data-grid-tree-field-container')!;
    this.fieldContainer.appendChild(this.input!);
  }

  #isExpanded(cell: IdsDataGridCell) {
    const dataCell = cell.dataGrid.rows[cell.rowIndex];
    if (dataCell) return stringToBool(dataCell.getAttribute(htmlAttributes.ARIA_EXPANDED));
    return false;
  }

  #isExpandable(cell: IdsDataGridCell) {
    const dataCell = cell.dataGrid.rows[cell.rowIndex];
    if (dataCell) return dataCell.childElementCount;
    return 0;
  }

  destroy() {
    super.destroy();
    this.expandButton?.remove();
    this.fieldContainer?.remove();
    this.expandButton = undefined;
    this.fieldContainer = undefined;
  }
}

export class MultiselectEditor implements IdsDataGridEditor {
  type = 'multiselect';

  input?: IdsMultiselect;

  clickEvent?: MouseEvent;

  #value?: string[];

  async init(cell?: IdsDataGridCell) {
    const isInline = cell?.column.editor?.inline;
    const settings = { ...cell?.column?.editor?.editorSettings };
    const hasBeforeShowFn = !!settings.beforeShow;
    const settingsOptions = <any[]>settings?.options ?? [];

    this.input = <IdsMultiselect>document.createElement('ids-multiselect');

    if (!cell) {
      return;
    }

    const cellData: IdsDropdownOptions = cell.dataGrid.data[cell.rowIndex][cell.column.id];
    const currentValue: string[] = cellData.map((data: IdsDropdownOption) => (data.value));
    if (currentValue) {
      this.#value = currentValue;
    } else {
      this.#value = [];
    }
    const selectedDefaultOptions = !hasBeforeShowFn ? [] : cellData.map((data) => ({
      value: data.value,
      label: data.label
    }));

    const listBox = document.createElement('ids-list-box');
    this.input.insertAdjacentElement('beforeend', listBox);

    delete settings.options;
    applyEditorSettings(this.input, settings);
    cell!.innerHTML = '';
    cell!.appendChild(this.input);
    cell!.classList.add('is-focused');

    this.input.loadDataSet([...settingsOptions, ...selectedDefaultOptions]
      .map((option) => ({ ...option, isCheckbox: true })));

    this.input.size = 'full';
    this.input.labelState = 'collapsed';
    this.input.classList.add('datagrid-editor');
    this.input.colorVariant = isInline ? 'in-cell' : 'borderless';
    this.input.fieldHeight = String(cell?.dataGrid?.rowHeight) === 'xxs' ? `xs` : String(cell?.dataGrid?.rowHeight);
    this.input.dropdownList?.listBox?.setAttribute(attributes.FIELD_HEIGHT, 'sm');
    this.input.value = this.#value;
    cell?.focus();

    if (this.clickEvent) {
      await this.input?.open();
    }
  }

  onEndCellEdit(cell?: IdsDataGridCell) {
    if (!cell) return;

    cell.focus();
  }

  save() {
    const selectedOptions = this.input?.selectedOptions
      .map((option) => ({ value: option.value!, label: option.label! })) || [];
    return {
      value: selectedOptions,
      dirtyCheckValue: selectedOptions
    };
  }

  destroy(): void {
    this.input?.offEvent('selected');
    this.#value = undefined;
  }

  value() {
    return this.input?.value ?? [];
  }

  change(newValue: boolean | number | string) {
    if (this.input) {
      this.input.value = Array.isArray(newValue) ? newValue : [String(newValue)];
    }
  }
}

export class LookupEditor implements IdsDataGridEditor {
  type = 'lookup';

  input?: IdsLookup;

  popup?: IdsModal;

  clickEvent?: MouseEvent;

  /**
   * Create an input and set the value and focus states
   * @param {IdsDataGridCell} cell the cell element
   */
  init(cell?: IdsDataGridCell) {
    const isInline = cell?.column.editor?.inline;
    this.input = <IdsLookup>document.createElement('ids-lookup');
    this.input.size = isInline ? 'full' : '';

    this.input.clearable = true;
    this.input.labelState = 'collapsed';

    // Clear cell and set value
    const value = cell?.innerText ?? '';
    cell!.innerHTML = '';
    cell?.appendChild(this.input as any);
    this.input.addEventListener('keydown', (e) => applyEditorEndCellEdit(cell!, e));

    this.input.value = value;

    this.popup = this.input.modal ?? undefined;

    applyEditorSettings(this.input, { ...cell?.column.editor?.editorSettings });
    applyEditorValidation(this.input?.input, cell?.column.editor?.editorValidation);
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    applyEditorPopupFocus(this);

    if (this.input?.triggerField) {
      this.input.triggerField.labelState = 'collapsed';
      this.input.triggerField.colorVariant = isInline ? 'in-cell' : 'borderless';
      this.input.triggerField.focus();
      this.input.triggerField.offEvent('paste', this.input.triggerField.input);
      this.input.triggerField.onEvent('paste', this.input.triggerField.input, (e) => {
        e.stopPropagation();
      });
    }

    const rowHeight = String(cell?.dataGrid?.rowHeight);

    // TODO: these are hacks... we should fix styles for xxs and not cheat by giving them styles for xs
    this.input.fieldHeight = rowHeight === 'xxs' ? `xs` : rowHeight;
    this.input.triggerField?.toggleAttribute(attributes.NO_MARGINS, rowHeight !== 'xxs');
    this.input.triggerField?.clearableButton?.toggleAttribute(attributes.NO_MARGINS, rowHeight !== 'xxs');
    this.input.triggerButton?.toggleAttribute(attributes.NO_MARGINS, rowHeight !== 'xxs');
  }

  value() {
    return this.input?.value ?? '';
  }

  change(newValue: boolean | number | string) {
    if (this.input) this.input.value = String(newValue);
  }

  /* Save selected dropdown value */
  save(): IdsDataGridSaveValue | undefined | null {
    return {
      value: this.input?.value,
      dirtyCheckValue: this.input?.input?.value
    };
  }

  /* Destroy the editor */
  destroy() {
    this.input?.offEvent('keydown');
    this.input?.offEvent('change');
    this.input = undefined;
  }
}

export const editors: Array<{ type: string, editor?: IdsDataGridEditor }> = [];

editors.push({
  type: 'input',
  editor: new InputEditor()
});

editors.push({
  type: 'password',
  editor: new PasswordInputEditor()
});

editors.push({
  type: 'checkbox',
  editor: new CheckboxEditor()
});

editors.push({
  type: 'dropdown',
  editor: new DropdownEditor()
});

editors.push({
  type: 'datepicker',
  editor: new DatePickerEditor()
});

editors.push({
  type: 'timepicker',
  editor: new TimePickerEditor()
});

editors.push({
  type: 'tree',
  editor: new TreeEditor()
});

editors.push({
  type: 'multiselect',
  editor: new MultiselectEditor()
});

editors.push({
  type: 'lookup',
  editor: new LookupEditor()
});
